/**
 * ────────────────────────────────────────────────────────────────────────────
 *  SINGLE SOURCE OF TRUTH FOR ALL ENVIRONMENTS
 * ────────────────────────────────────────────────────────────────────────────
 *  Every environment's API URL + Firebase project lives here. To point the app
 *  at a different backend, change ONE thing — the ACTIVE_ENV flag at the bottom —
 *  then `npm start`. For CI/CD builds, pin an env explicitly with
 *  `ng build --configuration <local|dev|stage|prod>` (which file-replaces
 *  environment.ts and ignores the flag).
 *
 *  None of this is secret: Firebase *web* config (apiKey, appId, …) and public
 *  API URLs are meant to ship in the client. Real secrets stay server-side.
 * ────────────────────────────────────────────────────────────────────────────
 */

export type EnvName = 'local' | 'dev' | 'stage' | 'prod';

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

export interface AppEnvironment {
  /** Which environment this is (handy for diagnostics / conditionals). */
  name: EnvName;
  production: boolean;
  /** Spring Boot API origin the frontend calls. */
  apiBaseUrl: string;
  /**
   * The two front-end origins. The app is split across two hosts: the marketing site and
   * the Quine dashboard/app. In prod these are a real subdomain pair (automaeta.com /
   * quine.automaeta.com); in dev/stage they're two separate Firebase Hosting site URLs
   * (no real subdomain — DNS for quine.* only exists in prod). Cross-host redirects + the
   * host guards read these instead of hardcoded `quine.localhost`.
   */
  marketingOrigin: string;
  appOrigin: string;
  /** Local-only: skip Firebase sign-in and forward a local headers file instead. */
  localAuthBypass: boolean;
  /** Which assets/local-auth/<user>.json to use when bypass is on. */
  localAuthUser: string;
  /** Firebase project this env signs into. */
  firebase: FirebaseConfig;
}

// ── Firebase projects (one per environment) ───────────────────────────────────
const FIREBASE_DEV: FirebaseConfig = {
  apiKey: 'AIzaSyBVcj3qGdotM22AB_INgKIXD-vumj3GMQM',
  authDomain: 'automaeta-dev-103b3.firebaseapp.com',
  projectId: 'automaeta-dev-103b3',
  storageBucket: 'automaeta-dev-103b3.firebasestorage.app',
  messagingSenderId: '430456356540',
  appId: '1:430456356540:web:d6a20ee94cc0dfb6fa1ab9',
  measurementId: 'G-8WFD4L04SB',
};

const FIREBASE_STAGE: FirebaseConfig = {
  apiKey: 'AIzaSyDdAlibP-Uf-Op7gHiVPnUUIzMMS3nnxSM',
  authDomain: 'automaeta-stage.firebaseapp.com',
  projectId: 'automaeta-stage',
  storageBucket: 'automaeta-stage.firebasestorage.app',
  messagingSenderId: '342674289907',
  appId: '1:342674289907:web:6571874e0f04c822f2f386',
};

const FIREBASE_PROD: FirebaseConfig = {
  apiKey: 'AIzaSyDYlkrx2YDX0I2lbF3lwr1svCfxF1fVdVs',
  authDomain: 'automaeta-prod.firebaseapp.com',
  projectId: 'automaeta-prod',
  storageBucket: 'automaeta-prod.firebasestorage.app',
  messagingSenderId: '401423950814',
  appId: '1:401423950814:web:1d1412711bd52247d4b34f',
};

// ── The environments ──────────────────────────────────────────────────────────
export const ENVIRONMENTS: Record<EnvName, AppEnvironment> = {
  // Pure local dev: API on your machine (IntelliJ/localdev, Mongo), auth bypassed
  // via a local headers file. No real Firebase sign-in. Firebase config is the dev
  // project's only so initializeApp() has something valid.
  local: {
    name: 'local',
    production: false,
    apiBaseUrl: 'http://localhost:9000',
    // Same ng serve on :4200; the `quine.` subdomain of localhost resolves to loopback.
    marketingOrigin: 'http://localhost:4200',
    appOrigin: 'http://kytelos.localhost:4200',
    localAuthBypass: true,
    localAuthUser: 'user1',
    firebase: FIREBASE_DEV,
  },

  // Deployed DEV backend on Hostinger KVM1 (Docker, Firestore + dev Firebase Auth).
  // Real Google/Firebase sign-in; the ID token is verified by the dev API.
  // dev is served from Cloudflare Pages (Firebase Hosting retired — it can't be
  // IP/identity-gated). Single host: marketing at /, app at /v2/**.
  dev: {
    name: 'dev',
    production: false,
    // HTTPS via Caddy reverse proxy on KVM1 (→ 127.0.0.1:9000). Needs DNS:
    // dev-api.automaeta.com A 2.25.185.154. (Raw http://IP:9000 = mixed-content from Firebase.)
    apiBaseUrl: 'https://dev-api.automaeta.com',
    // Single Firebase Hosting site for dev (automaeta-dev-103b3.web.app). marketing === app
    // ⇒ single-host mode: the host guards serve both halves on one domain (no subdomain split,
    // no cross-redirects). Split into two sites later by giving these different URLs.
    marketingOrigin: 'https://automaeta-marketing.pages.dev',
    appOrigin: 'https://automaeta-marketing.pages.dev',
    localAuthBypass: false,
    localAuthUser: 'user1',
    firebase: FIREBASE_DEV,
  },

  // Stage backend + stage Firebase project. TODO: set apiBaseUrl to the deployed
  // stage API origin once it exists.
  stage: {
    name: 'stage',
    production: true,
    apiBaseUrl: 'https://stage-api.automaeta.com', // TODO: real stage API origin
    // TODO: two Firebase Hosting sites in automaeta-stage. Replace with real *.web.app URLs.
    marketingOrigin: 'https://automaeta-stage-marketing.web.app',
    appOrigin: 'https://automaeta-stage-app.web.app',
    localAuthBypass: false,
    localAuthUser: 'user1',
    firebase: FIREBASE_STAGE,
  },

  // Prod backend + prod Firebase project. TODO: set apiBaseUrl to the deployed
  // prod API origin (Hostinger KVM2 behind Caddy) once it exists.
  prod: {
    name: 'prod',
    production: true,
    apiBaseUrl: 'https://api.automaeta.com', // TODO: real prod API origin
    // The real subdomain pair (DNS configured for prod only). Confirm before prod release.
    marketingOrigin: 'https://automaeta.com',
    appOrigin: 'https://quine.automaeta.com',
    localAuthBypass: false,
    localAuthUser: 'user1',
    firebase: FIREBASE_PROD,
  },
};

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  THE ENV FLAG — change this one value to switch which backend `npm start`  ║
 * ║  (default `ng serve`) points at: 'local' | 'dev' | 'stage' | 'prod'.       ║
 * ║  CI/CD builds override it with `ng build -c <env>` and ignore this.        ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export const ACTIVE_ENV: EnvName = 'local';
